import { IDENTITY_CONFIG, METADATA_OIDC } from '../utils/authConst';
import { UserManager, WebStorageStateStore, Log } from 'oidc-client';

export default class AuthService {
  UserManager;

  constructor() {
    this.UserManager = new UserManager({
      ...IDENTITY_CONFIG,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      metadata: {
        ...METADATA_OIDC,
      },
    });
    // Logger
    Log.logger = console;
    Log.level = Log.DEBUG;
    this.UserManager.events.addUserLoaded((user) => {
      if (window.location.href.includes('callback')) {
        this.navigateToScreen();
      }
    });

    this.UserManager.events.addAccessTokenExpired(() => {
      void this.signInSilent();
    });
  }

  signInRedirectCallback = (): any => {
    localStorage.setItem('loggedIn Time', `${String(new Date())}`);
    localStorage.setItem('Notify', 'Yes');
    return this.UserManager.signinRedirectCallback();
  };

  getUser = async (): Promise<any> => {
    const user = await this.UserManager.getUser();

    if (user == null) {
      return await this.UserManager.signinRedirectCallback();
    }
    return user;
  };

  parseJwt = (token: string): any => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64));
  };

  signInRedirect = async (): Promise<void> => {
    localStorage.setItem('redirectUri', window.location.pathname);
    void this.UserManager.signinRedirect({});
  };

  navigateToScreen = (): void => {
    window.location.replace('/');
  };

  isAuthenticated = (): boolean => {
    const oidcStorage = localStorage.getItem(
      `oidc.user:${IDENTITY_CONFIG.authority ?? ''}:${IDENTITY_CONFIG.client_id ?? ''}`
    );

    if (oidcStorage != null) {
      const parsedStorage = JSON.parse(oidcStorage);
      return !!(Boolean(parsedStorage) && Boolean(parsedStorage.access_token));
    }

    return false;
  };

  signInSilent = async (): Promise<string | undefined | void> => {
    return this.UserManager.signinSilent({
      scope: IDENTITY_CONFIG.scope,
      response_type: IDENTITY_CONFIG.response_type,
    })
      .then(async (user) => {
        await this.UserManager.signinSilentCallback();
        return user?.access_token;
        // void this.UserManager.signinRedirect({});
      })
      .catch(() => {
        void this.logout();
      });
  };

  signinSilentCallback = async (): Promise<void> => {
    await this.UserManager.signinSilentCallback();
  };

  createSignInRequest = (): any => {
    return this.UserManager.createSigninRequest();
  };

  logout = async (): Promise<void> => {
    // Set a timestamp to notify other tabs
    localStorage.setItem('logout_event', Date.now().toString());

    void this.UserManager.signoutRedirect({
      id_token_hint: localStorage.getItem('id_token'),
    });
    // localStorage.removeItem('loggedIn Time');
    // localStorage.removeItem('Notify');
    // localStorage.removeItem('tenantId');
    localStorage.clear();
    sessionStorage.clear();
    void this.UserManager.clearStaleState();
  };

  logoutAndRedirect = async (redirectUri?: string): Promise<void> => {
    const url =
      process.env.REACT_APP_AUTH_URL + `/Account/LoggedOut?PostLogoutRedirectUri=${redirectUri}`;
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace(url);
  };

  delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  passwordLessLogin = async (redirectUri: string, intentUrl: string): Promise<void> => {
    await this.UserManager.signoutRedirectCallback();
    localStorage.clear();
    sessionStorage.clear();
    // sessionStorage.removeItem('chat-session-id');
    // sessionStorage.removeItem('sentryReplaySession');
    window?.localStorage?.setItem('intentUrl', intentUrl);
    window.location.href = redirectUri;
  };

  signOutRedirectCallback = async (): Promise<void> => {
    try {
      await this.UserManager.signoutRedirectCallback();
      localStorage.clear();
      window.location.replace(process.env.REACT_APP_PUBLIC_URL ?? '');
    } catch (err) {
      // Handle the error if necessary
    }

    await this.UserManager.clearStaleState();
  };

  getToken = (): string => {
    const oidcStorageString = localStorage.getItem(
      `oidc.user:${IDENTITY_CONFIG.authority ?? ''}:${IDENTITY_CONFIG.client_id ?? ''}`
    );
    const oidcStorage = oidcStorageString != null ? JSON.parse(oidcStorageString) : null;
    return oidcStorage != null && Boolean(oidcStorage.access_token) ? oidcStorage.access_token : '';
  };
  getIdToken = (): string => {
    const oidcStorageString = localStorage.getItem(
      `oidc.user:${IDENTITY_CONFIG.authority ?? ''}:${IDENTITY_CONFIG.client_id ?? ''}`
    );
    const oidcStorage = oidcStorageString != null ? JSON.parse(oidcStorageString) : null;
    const idToken =
      oidcStorage != null && Boolean(oidcStorage.id_token) ? oidcStorage.id_token : null;
    return idToken;
  };

  getTokenForSignalR = async (): Promise<string> => {
    if (this.isTokenExpired()) {
      return (await this.signInSilent()) as string;
    }
    return this.getToken();
  };

  isTokenExpired = (): boolean => {
    const token = this.getIdToken();
    if (!token) return true;

    try {
      // Get the payload part of the JWT token
      const base64Url = token.split('.')[1];
      if (!base64Url) return true;

      // Convert base64 to JSON
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));

      // Check if the token has an expiration time
      if (!payload.exp) return true;

      // Convert expiration time to milliseconds and compare with current time
      const expirationTime = payload.exp * 1000; // exp is in seconds, convert to milliseconds
      const currentTime = Date.now();

      return currentTime >= expirationTime;
    } catch (error) {
      return true; // If we can't decode the token, assume it's expired
    }
  };
}
